import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_DUTIES,
  READINESS_DEFS,
  READINESS_MAP,
  type SuccessionReadiness,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

interface Successor {
  id: string;
  employeeId: string;
  name: string;
  readiness: SuccessionReadiness;
  /*
    الخادمُ يرسل التسمية واللون جاهزين، وهذه الشاشة لا تستعملهما: تقرأ
    المفتاحَ من الكتالوج المشترك. فلو تغيّرت التسميةُ يومًا تغيّرت في
    الشاشتين معًا، ولا تبقى واحدةٌ تقول «جاهز الآن» والأخرى شيئًا آخر.
  */
  readinessLabel: string;
  readinessKind: string;
  gaps: string[];
  notes: string | null;
}

interface SuccessionRow {
  id: string;
  title: string;
  department: string | null;
  gradeName: string | null;
  gradeLevel: number | null;
  holders: { id: string; name: string }[];
  readyNow: number;
  successors: Successor[];
}

interface EmployeeLite {
  id: string;
  name: string;
  code: string;
}

/**
 * خطط الإحلال.
 *
 * ## الخبرُ هو الفراغ لا الخطّة
 *
 * شاشاتُ الإحلال في أغلب الأنظمة تعرض الخططَ المكتوبة فتبدو الشركةُ
 * مطمئنّة، والحقيقةُ أنّ المكتوبَ هو الجزءُ المحلول أصلًا. الخطرُ في
 * الوظائف التي لم يكتب لها أحدٌ شيئًا — ولأنّها لا تُعرض لا يسأل عنها
 * أحد. فرُتّبت هذه الصفحةُ عكسَ العادة: ما لا خلفَ له أوّلًا، ثمّ ما له
 * خلفٌ لم ينضج، ثمّ المطمئنّ في الآخر.
 *
 * والشاغرُ الأعلى درجةً أوّلُ الفارغين: خلوُّ وظيفةٍ قياديّةٍ بلا خلفٍ
 * مكتوبٍ يوقف قرارًا، وخلوُّ وظيفةٍ تنفيذيّةٍ يؤخّر عملًا.
 */
export function Succession() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { atLeast } = useAuth();
  const canEdit = atLeast('MANAGER');

  const [onlyGaps, setOnlyGaps] = useState(false);
  const [adding, setAdding] = useState<SuccessionRow | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'org', 'succession'],
    queryFn: () => api<SuccessionRow[]>('/hr/org/succession'),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/hr/org/successors/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast('حُذف المرشّح');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف'),
  });

  const rows = list.data ?? [];

  const noPlan = rows.filter((r) => r.successors.length === 0).length;
  const notReady = rows.filter(
    (r) => r.successors.length > 0 && r.readyNow === 0,
  ).length;
  const covered = rows.filter((r) => r.readyNow > 0).length;
  /* الشاغرةُ بلا خلفٍ فراغٌ قائمٌ الآن لا احتمالٌ في المستقبل. */
  const vacantNoPlan = rows.filter(
    (r) => r.successors.length === 0 && r.holders.length === 0,
  ).length;

  const rank = (r: SuccessionRow) =>
    r.successors.length === 0 ? 0 : r.readyNow === 0 ? 1 : 2;

  const shown = rows
    .filter((r) => !onlyGaps || r.successors.length === 0)
    .slice()
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (b.gradeLevel ?? 0) - (a.gradeLevel ?? 0) ||
        a.title.localeCompare(b.title, 'ar'),
    );

  return (
    <div className="page">
      <PageHead
        title="خطط الإحلال"
        subtitle="من يشغل كلّ وظيفة لو خلت غدًا"
        actions={
          <Link className="btn" to="/hr/org/positions">
            <Icon name="clipboard" size={15} />
            الأوصاف الوظيفية
          </Link>
        }
      />

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {list.data && rows.length > 0 && (
        <div className="stats">
          <Stat
            label="وظائف بلا خلف"
            value={num(noPlan)}
            note={
              vacantNoPlan > 0
                ? `منها ${num(vacantNoPlan)} شاغرةٌ الآن`
                : noPlan > 0
                  ? 'لو خلت غدًا بدأ البحث من الصفر'
                  : undefined
            }
          />
          <Stat
            label="جاهز الآن"
            value={num(covered)}
            note="وظيفةٌ لها مرشّحٌ يشغلها غدًا"
          />
          <Stat
            label="خطّةٌ لم تنضج"
            value={num(notReady)}
            note={notReady > 0 ? 'لها مرشّح، ولا أحد جاهزًا بعد' : undefined}
          />
          <Stat
            label="الوظائف"
            value={num(rows.length)}
            note={plural(rows.length, WORDS.job)}
          />
        </div>
      )}

      {/*
        حدّان يُقالان ولا يُخفيان: الجاهزيةُ هنا حكمُ مديرٍ يكتبه بيده،
        ولا يقرؤها النظامُ من تقييمٍ ولا من مهارةٍ مسجّلة. والقائمةُ
        كلُّها مبنيّةٌ على الأوصاف الوظيفية — فوظيفةٌ يشتغل بها الناس
        ولم يُكتب لها وصفٌ لا تظهر هنا أصلًا، ولا يُحسب فراغُها.
      */}
      <Notice title="ما لا وصفَ له لا يظهر في هذه القائمة" icon="info">
        الوظائفُ هنا هي المكتوبةُ في{' '}
        <a className="link" href="/hr/org/positions">
          الأوصاف الوظيفية
        </a>
        . فمن يعمل بمسمًّى لم يُكتب له وصفٌ بعد لا وظيفةَ له في هذه الشاشة،
        ولا يُحسب في «وظائف بلا خلف». والجاهزيةُ تقديرُ مديرٍ يكتبه، لا
        نتيجةُ قياسٍ يحسبها النظام — وتبقى كما كُتبت حتى يعدّلها أحد.
      </Notice>

      {rows.length > 0 && (
        <div className="toolbar">
          <button
            className={`chip${onlyGaps ? ' chip-on' : ''}`}
            onClick={() => setOnlyGaps((v) => !v)}
          >
            بلا خلف فقط
            {noPlan > 0 && <span className="num"> · {num(noPlan)}</span>}
          </button>
        </div>
      )}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا أوصاف وظيفية بعد"
          text="الإحلالُ خلافةُ وظيفةٍ لا خلافةُ شخص — وبلا وصفٍ وظيفيٍّ مكتوبٍ لا يوجد ما يُخلَف."
          action={
            <Link className="btn btn-primary" to="/hr/org/positions">
              اكتب أوّل وصف
            </Link>
          }
        />
      )}

      {rows.length > 0 && shown.length === 0 && (
        <EmptyState
          title="لكلّ وظيفة خلفٌ مكتوب"
          text="لا وظيفةَ بلا مرشّح. وبقي السؤالُ الثاني: كم منهم جاهزٌ فعلًا الآن؟"
          action={
            <button className="btn" onClick={() => setOnlyGaps(false)}>
              اعرض الكلّ
            </button>
          }
        />
      )}

      {shown.map((r) => {
        const meta = [
          r.department,
          r.gradeName,
          r.holders.length > 0
            ? `يشغلها ${r.holders.map((h) => h.name).join('، ')}`
            : 'شاغرةٌ الآن',
        ].filter(Boolean);

        /* الترتيبُ داخل البطاقة بالجاهزية: الجاهزُ أوّلًا كما في الكتالوج. */
        const successors = r.successors
          .slice()
          .sort(
            (a, b) =>
              READINESS_MAP[a.readiness].order - READINESS_MAP[b.readiness].order,
          );

        return (
          <div className="card" key={r.id}>
            <div className="card-head">
              <div className="grow" style={{ minWidth: 0 }}>
                <h2 className="card-title">
                  <Link className="link" to={`/hr/org/positions/${r.id}`}>
                    {r.title}
                  </Link>
                </h2>
                <div className="tiny mute">{meta.join(' · ')}</div>
              </div>

              {r.successors.length === 0 ? (
                <Badge kind="danger">بلا خلف</Badge>
              ) : r.readyNow > 0 ? (
                <Badge kind="success">جاهز الآن: {num(r.readyNow)}</Badge>
              ) : (
                <Badge kind="warning">لا أحد جاهز الآن</Badge>
              )}

              {canEdit && (
                <button
                  className="btn btn-sm"
                  onClick={() => setAdding(r)}
                >
                  <Icon name="plus" size={13} />
                  أضف مرشّحًا
                </button>
              )}
            </div>

            {successors.length === 0 ? (
              <div className="card-body">
                <p className="small">
                  لا مرشّحَ مكتوبٌ لهذه الوظيفة. ومن يشغلها اليوم قد يستقيل أو
                  يمرض أو يُرقّى، وحينها يبدأ البحثُ من الصفر في أسوأ وقت.
                </p>
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>المرشّح</th>
                      <th>الجاهزية</th>
                      <th>ما ينقصه</th>
                      {canEdit && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {successors.map((s) => {
                      const def = READINESS_MAP[s.readiness];
                      return (
                        <tr key={s.id}>
                          <td>
                            <Link
                              to={`/hr/employees/${s.employeeId}`}
                              className="cell-strong link"
                            >
                              {s.name}
                            </Link>
                            {s.notes && (
                              <div className="tiny mute">{s.notes}</div>
                            )}
                          </td>
                          <td>
                            <Badge kind={def.kind}>{def.label}</Badge>
                          </td>
                          <td>
                            {s.gaps.length === 0 ? (
                              <span className="cell-mute">
                                {s.readiness === 'NOW'
                                  ? '—'
                                  : 'لم يُكتب ما ينقصه'}
                              </span>
                            ) : (
                              <span
                                className="row"
                                style={{ gap: 6, flexWrap: 'wrap' }}
                              >
                                {s.gaps.map((g, i) => (
                                  <Badge key={i} kind="neutral">
                                    {g}
                                  </Badge>
                                ))}
                              </span>
                            )}
                          </td>
                          {canEdit && (
                            <td>
                              <div
                                className="row"
                                style={{ gap: 6, justifyContent: 'flex-end' }}
                              >
                                <button
                                  className="btn btn-sm btn-icon"
                                  aria-label={`احذف ${s.name} من خلفاء ${r.title}`}
                                  disabled={remove.isPending}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `حذف ${s.name} من خلفاء «${r.title}»؟`,
                                      )
                                    ) {
                                      remove.mutate(s.id);
                                    }
                                  }}
                                >
                                  <Icon name="trash" size={13} />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {adding && (
        <SuccessorDialog position={adding} onClose={() => setAdding(null)} />
      )}
    </div>
  );
}

/**
 * إضافةُ مرشّحٍ لخلافة وظيفة.
 *
 * ومن ليس جاهزًا الآن يُكتب ما ينقصه: «جاهزٌ خلال سنة» بلا ذكرِ الناقص
 * جملةٌ لا يُبنى عليها تدريب. والقاعدةُ نفسها في الخادم — تُطبَّق هنا
 * كذلك ليرى الكاتبُ سببَ التعطيل قبل أن يضغط، لا رسالةَ خطأٍ بعده.
 */
function SuccessorDialog({
  position,
  onClose,
}: {
  position: SuccessionRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [employeeId, setEmployeeId] = useState('');
  const [readiness, setReadiness] = useState<SuccessionReadiness>('SOON');
  const [notes, setNotes] = useState('');
  const [gaps, setGaps] = useState<string[]>(['']);

  const employees = useQuery({
    queryKey: ['employees', { pageSize: 200, status: 'ACTIVE' }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>('/hr/employees?pageSize=200&status=ACTIVE'),
  });

  /* من يشغلها لا يخلف نفسه، والمكتوبُ مرّةً لا يُكتب مرّتين. */
  const taken = new Set([
    ...position.successors.map((s) => s.employeeId),
    ...position.holders.map((h) => h.id),
  ]);
  const options = (employees.data?.items ?? []).filter((e) => !taken.has(e.id));

  const clean = gaps.map((g) => g.trim()).filter(Boolean);

  const add = useMutation({
    mutationFn: () =>
      api(`/hr/org/positions/${position.id}/successors`, {
        method: 'POST',
        body: {
          employeeId,
          readiness,
          notes: notes.trim() || null,
          gaps: clean,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast('أُضيف المرشّح');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const needsWhy = readiness !== 'NOW' && clean.length === 0 && !notes.trim();
  const valid = employeeId !== '' && !needsWhy && clean.length <= MAX_DUTIES;

  return (
    <Modal
      title={`مرشّح لخلافة ${position.title}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => add.mutate()}
            disabled={!valid || add.isPending}
          >
            {add.isPending ? 'جارٍ الحفظ' : 'أضف'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="sc-employee">
            الموظّف
          </label>
          <select
            id="sc-employee"
            className="select"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            <option value="">اختر</option>
            {options.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          {employees.data && options.length === 0 && (
            <span className="tiny mute">
              لا أحدَ يُضاف: كلُّ من في القائمة إمّا يشغل الوظيفةَ أو مكتوبٌ
              مرشّحًا لها.
            </span>
          )}
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="sc-readiness">
            الجاهزية
          </label>
          <select
            id="sc-readiness"
            className="select"
            value={readiness}
            onChange={(e) =>
              setReadiness(e.target.value as SuccessionReadiness)
            }
          >
            {READINESS_DEFS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {READINESS_MAP[readiness].description}
          </span>
        </div>

        <div className="field span-2">
          <label className="label">ما ينقصه ليكون جاهزًا</label>
          {gaps.map((g, i) => (
            <div className="row" key={i}>
              <input
                className="input grow"
                value={g}
                maxLength={300}
                placeholder="مثلًا: إدارة فريق، أو عرضٌ أمام العملاء"
                onChange={(e) =>
                  setGaps((prev) =>
                    prev.map((v, j) => (j === i ? e.target.value : v)),
                  )
                }
              />
              <button
                className="btn btn-sm btn-icon"
                aria-label="احذف السطر"
                onClick={() =>
                  setGaps((prev) => prev.filter((_, j) => j !== i))
                }
              >
                <Icon name="minus" size={13} />
              </button>
            </div>
          ))}
          <button
            className="btn btn-sm"
            disabled={gaps.length >= MAX_DUTIES}
            onClick={() => setGaps((prev) => [...prev, ''])}
          >
            <Icon name="plus" size={13} />
            سطر
          </button>
          <span className="tiny mute">
            كلُّ سطرٍ بندٌ في خطّة تدريبه. والفارغُ يُهمل عند الحفظ.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="sc-notes">
            ملاحظة
          </label>
          <textarea
            id="sc-notes"
            className="input"
            rows={3}
            maxLength={2000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="تُقرأ عند مراجعة الخطّة."
          />
          {needsWhy && (
            <span className="tiny mute">
              من ليس جاهزًا الآن يُكتب ما ينقصه — سطرٌ واحدٌ على الأقلّ أو
              ملاحظة.
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}
